import { Navigate } from "react-router";
import { useMe } from "@/lib/auth";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { MinimalNav } from "@/components/MinimalNav";
import { MinimalFooter } from "@/components/MinimalFooter";
import { LandingHeroLottie } from "@/components/LandingHeroLottie";
import { StatStrip } from "@/components/StatStrip";
import { getTenantConfig } from "@/lib/tenant";

export function Landing() {
  const tenant = getTenantConfig();
  const isMindfulMe = tenant.brandName === "MindfulMe";

  const pageTitle = isMindfulMe
    ? "MindfulMe · 8 Expressions of Love & Relationship Insights"
    : "Kidology · Practical, science-backed courses for parents";

  const pageDescription = isMindfulMe
    ? "Discover your unique profile for expressing and receiving affection — 8 Expressions of Love assessment report and relationship insights."
    : "Practical, science-backed courses for the moments that feel impossible — meltdowns, screens, sleep, big feelings. Watch on your phone, in the bits of time you actually have.";

  useDocumentMeta({ title: pageTitle, description: pageDescription });

  const { data, isLoading } = useMe();

  if (isLoading) {
    return (
      <div
        className="min-h-dvh bg-background"
        aria-busy="true"
        aria-label="Loading"
      />
    );
  }

  // Returning user; bounce to target surface.
  if (data) {
    return <Navigate to={tenant.homePath} replace />;
  }

  // Anonymous path — the billboard landing page.
  return (
    <div className="min-h-dvh bg-background">
      <MinimalNav />

      <section className="mx-auto max-w-[1180px] px-5 pt-12 pb-4 md:px-8">
        <div className="grid items-center gap-12 md:grid-cols-[1.05fr_1fr]">
          {/* Left column — copy */}
          <div>
            <span className="mb-5 inline-block rounded-full bg-primary/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-primary">
              {isMindfulMe ? "For couples & individuals, by relationship experts" : "For parents, by a clinician"}
            </span>
            <h1 className="mb-6 text-4xl font-extrabold leading-[1.1] tracking-tight text-text-strong md:text-6xl">
              {isMindfulMe ? (
                <>
                  Love is a journey.
                  <br />
                  Understand each other <span className="text-primary">better</span>.
                </>
              ) : (
                <>
                  Parenting is hard.
                  <br />
                  You're <span className="text-primary">not</span> alone in it.
                </>
              )}
            </h1>
            <p className="max-w-[480px] text-lg leading-[1.65] text-muted-foreground">
              {pageDescription}
            </p>
          </div>

          {/* Right column — illustrated Lottie (centered on mobile) */}
          <div className="flex justify-center md:justify-end">
            <LandingHeroLottie alt={isMindfulMe ? "Two people sharing affection" : "A parent and child together"} />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-[1180px] px-5 md:px-8">
        <StatStrip>
          {isMindfulMe ? (
            <>
              <StatStrip.Item value="8" label="Love Expressions analyzed" />
              <StatStrip.Item value="100%" label="Personalized report" />
              <StatStrip.Item value="5–10 min" label="Assessment duration" />
              <StatStrip.Item value="100%" label="Mobile-friendly" />
            </>
          ) : (
            <>
              <StatStrip.Item value="24" label="Courses, free to watch" />
              <StatStrip.Item value="6" label="Core topics covered" />
              <StatStrip.Item value="5–12 min" label="Average lesson length" />
              <StatStrip.Item value="100%" label="Mobile-friendly" />
            </>
          )}
        </StatStrip>
      </section>

      <MinimalFooter compact />
    </div>
  );
}
